import { uniq } from "es-toolkit";

import { getComputedResultNode } from "./utils";

import type { DocumentModel } from "../DocumentModel";
import type { DocumentLayout } from "./types";
import type { ComputedReferenceNodeModel } from "@/models/nodes/ComputedReferenceNodeModel";
import type { ComputedResultNodeModel } from "@/models/nodes/ComputedResultNodeModel";
import type { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";

/**
 * Convergent collaboration heal, run on every client (local and remote).
 * Concurrent peers converge instead of inserting duplicates:
 */
export function healComputedChildren(doc: DocumentModel, layout: DocumentLayout): boolean {
  const ops: (() => void)[] = [];

  for (const wrapper of layout.wrappers) {
    if (!wrapper.computed) continue;
    collectResultNodeDedupe(wrapper, ops);
    const result = getComputedResultNode(wrapper);
    if (result) collectInlinePointerHeal(result, ops);
  }

  for (const ref of doc.getNodesByType("computedReference")) {
    collectInlinePointerHeal(ref, ops);
  }

  if (ops.length === 0) return false;

  doc.transact(
    () => {
      ops.forEach((op) => op());
    },
    `ComputedNodeManager:heal`,
    { runInAction: true, skipComputedRecalc: true },
  );
  return true;
}

function collectInlinePointerHeal(
  node: ComputedResultNodeModel | ComputedReferenceNodeModel,
  ops: (() => void)[],
): void {
  const { inlineId } = node.props;
  if (!inlineId) return;

  const ids = node.childrenIds;
  if (!ids.includes(inlineId) || ids.length === 1) return;

  ops.push(() => node.setChildren([inlineId]));
}

/**
 * Queue collapsing a result node whose id appears more than once in the equation
 * inline (a YJS array-merge artifact from concurrent creates), keeping the first
 * occurrence so rendering and lookups see a single result node.
 *
 * Note: this method might be redundant, not 100% sure - duplicated inlineIds are expected, but not the refIds.
 * May delete later.
 */
function collectResultNodeDedupe(wrapper: WrapperNodeModel, ops: (() => void)[]): void {
  const { inlineNode, refId } = wrapper;
  if (!inlineNode || !refId) return;

  const ids = inlineNode.childrenIds;
  const deduped = uniq(ids);
  if (deduped.length === ids.length) return;

  console.warn("[ComputedNodeManager] Deduplicating result node ids in wrapper");

  ops.push(() => inlineNode.setChildren(deduped));
}
